import type { PlayerID, PublicTeamID, TeamID } from '../ids';
import type { PlayerStatus } from '../player';
import type { FieldingPosition } from '../positions';

export type PlayResult = 'hit' | 'out';

export enum HomeRunLocation {
    LEFT_FIELD = 'left_field',
    CENTER_FIELD = 'center_field',
    RIGHT_FIELD = 'right_field',
    IN_THE_PARK = 'in_the_park',
}

export type SprayChartData = {
    id: string;
    code: string;
    location: {
        x: number;
        y: number;
    };
    playResult: PlayResult;
    hrLocation?: HomeRunLocation;
};

export type BasketBallStats = {
    averages: {
        playerStats: BasketBallAverages[];
        teamStats: BasketBallAverages[];
    };
    totals: {
        playerStats: BasketBallTotals[];
        teamStats: BasketBallTotals[];
    };
};

export type BasketBallEventStats = {
    playerStats: EventBasketballTotals[];
    cumulativeStats: EventBasketballTotals[];
};

export interface PlayerForStats {
    id?: PlayerID;
    teamID?: TeamID;
    name: string;
    number: string;
    firstName?: string;
    lastName?: string;
    status?: PlayerStatus;
    publicTeamID?: PublicTeamID;
}

export type CoreBattingStats = {
    '1B': number;
    '2B': number;
    '2OUTRBI': number;
    '2S+3%': number | string;
    '2S+3': number;
    '3B': number;
    '6+%': number | string;
    '6+': number;
    AB: number;
    'AB/HR': number | string;
    AVG: number | string;
    'BA/RISP': number | string;
    BABIP: number | string;
    BB: number;
    'BB/K': number | string;
    'C%': number | string;
    CI: number;
    CS: number;
    'FB%': number | string;
    FC: number;
    'GB%': number | string;
    GIDP: number;
    GITP: number;
    GP: number;
    H: number;
    HBP: number;
    HHB: number;
    HR: number;
    'K-L': number;
    'LD%': number | string;
    LOB: number;
    OBP: number | string;
    OPS: number | string;
    PA: number;
    'PA/BB': number | string;
    PIK: number;
    PS: number;
    'PS/PA': number | string;
    QAB: number;
    'QAB%': number | string;
    R: number;
    RBI: number;
    ROE: number;
    SAC: number;
    SB: number;
    'SB%': number | string;
    SF: number;
    SLG: number | string;
    SO: number;
    TB: number;
    XBH: number;
    LD: number;
    GB: number;
    FB: number;
};
export type TeamBattingStats = CoreBattingStats & { player: PlayerForStats };
export type EventBattingStats = CoreBattingStats & { event: string; opponent: string };

export type CorePitchingStats = {
    IP: number;
    'GP:P': number;
    GS: number;
    BF: number;
    '#P': number;
    W: number;
    L: number;
    SV: number;
    SVO: number;
    BS: number;
    'SV%': number | string;
    H: number;
    R: number;
    ER: number;
    BB: number;
    SO: number;
    'K-L': number;
    HBP: number;
    ERA: number | string;
    WHIP: number | string;
    LOB: number;
    BK: number;
    PIK: number;
    CS: number;
    SB: number;
    'SB%': number | string;
    WP: number;
    BAA: number | string;
    TB: number;
    TS: number;
    'P/IP': number | string;
    'P/BF': number | string;
    'LBFP#': string | number;
    '<3%': number | string;
    LOO: number;
    '1ST2OUT': number;
    '123INN': number;
    '<13': number;
    FIP: number | string;
    'S%': number | string;
    'FPS%': number | string;
    'FPSO%': number | string;
    'FPSW%': number | string;
    'FPSH%': number | string;
    'BB/INN': number | string;
    '0BBINN': number;
    BBS: number;
    LOBB: number;
    LOBBS: number;
    'SM%': number | string;
    'K/BF': number | string;
    'K/BB': number | string;
    'WEAK%': number | string;
    'HHB%': number | string;
    'GO/AO': number | string;
    HR: number;
    'LD%': number | string;
    'FB%': number | string;
    'GB%': number | string;
    BABIP: number | string;
    'BA/RISP': number | string;
    FB: number;
    FBS: number;
    'FBS%': number | string;
    'FBSW%': number | string;
    'FBSM%': number | string;
    CT: number;
    CTS: number;
    'CTS%': number | string;
    'CTSW%': number | string;
    'CTSM%': number | string;
    CB: number;
    CBS: number;
    'CBS%': number | string;
    'CBSW%': number | string;
    'CBSM%': number | string;
    SL: number;
    SLS: number;
    'SLS%': number | string;
    'SLSW%': number | string;
    'SLSM%': number | string;
    CH: number;
    CHS: number;
    'CHS%': number | string;
    'CHSW%': number | string;
    'CHSM%': number | string;
    OS: number;
    OSS: number;
    'OSS%': number | string;
    'OSSW%': number | string;
    'OSSM%': number | string;
    RB: number;
    RBS: number;
    'RBS%': number | string;
    'RBSW%': number | string;
    'RBSM%': number | string;
    DB: number;
    DBS: number;
    'DBS%': number | string;
    'DBSW%': number | string;
    'DBSM%': number | string;
    SC: number;
    SCS: number;
    'SCS%': number | string;
    'SCSW%': number | string;
    'SCSM%': number | string;
    DC: number;
    DCS: number;
    'DCS%': number | string;
    'DCSW%': number | string;
    'DCSM%': number | string;
    KB: number;
    KBS: number;
    'KBS%': number | string;
    'KBSW%': number | string;
    'KBSM%': number | string;
    KC: number;
    KCS: number;
    'KCS%': number | string;
    'KCSW%': number | string;
    'KCSM%': number | string;
    '<3': number;
    SM: number;
    SW: number;
    WHB: number;
    HHB: number;
    GO: number;
    AO: number;
    LD: number;
    GB: number;
    FLB: number;
    // Baseball velocity stats
    MPHFB: number | string;
    MPHCT: number | string;
    MPHCB: number | string;
    MPHSL: number | string;
    MPHCH: number | string;
    MPHOS: number | string;
    // Softball velocity stats
    MPHRB: number | string;
    MPHDB: number | string;
    MPHSC: number | string;
    MPHDC: number | string;
    MPHKB: number | string;
    MPHKC: number | string;
};
export type TeamPitchingStats = CorePitchingStats & { player: PlayerForStats };
export type EventPitchingStats = CorePitchingStats & { event: string; opponent: string };

export type CoreFieldingStats = {
    TC: number;
    A: number;
    PO: number;
    FPCT: number | string;
    E: number;
    DP: number;
    TP: number;
    'GP:F': number;
    INN: number;
    PB: number;
    SB: number;
    'SB-ATT': string;
    CS: number;
    'CS%': number | string;
    PIK: number;
    CI: number;
    'GP:C': number;
};
export type TeamFieldingStats = CoreFieldingStats & { player: PlayerForStats };
export type EventFieldingStats = CoreFieldingStats & { event: string; opponent: string };

export type CoreInningsPlayedStats = {
    IP: number;
    'IP:F': number;
    'IC:C': number;
    'IP:1B': number;
    'IP:2B': number;
    'IP:3B': number;
    'IP:SS': number;
    'IP:LF': number;
    'IP:CF': number;
    'IP:RF': number;
    'IP:SF': number;
};
export type EventInningsPlayedStats = CoreInningsPlayedStats & { event: string; opponent: string };
export type TeamInningsPlayedStats = CoreInningsPlayedStats & { player: PlayerForStats };

/** One row of the fielding "stats by position" view: season fielding totals for a single position. */
export type FieldingByPositionStats = {
    position: FieldingPosition;
    INN: number;
    TC: number;
    A: number;
    PO: number;
    FPCT: number | string;
    E: number;
    DP: number;
    TP: number;
};

export type CoreBasketballAverages = {
    GP: number;
    APG: number;
    'FG%': number;
    'FT%': number;
    PPG: number;
    RPG: number;
    SPG: number;
    BPG: number;
    '3PT%': number;
    PFPG: number;
    TOPG: number;
    'AST/TO': number;
    'eFG%': number;
    'TS%': number;
};
export type BasketBallAverages = CoreBasketballAverages & { player: PlayerForStats };

export type CoreBasketballTotals = {
    GP: number | null;
    GS: number | null;
    PTS: number;
    PF: number;
    FG: string | number;
    '3PT': string | number;
    FT: string | number;
    TO: number;
    REB: number;
    AST: number;
    STL: number;
    BLK: number;
};
export type BasketBallTotals = CoreBasketballTotals & { player: PlayerForStats };

export type EventBasketballTotals = {
    event: string;
    opponent: string;
    PTS: number;
    PF: number;
    FG: string | number;
    'FG%': number | null | undefined;
    '3PT': string | number;
    '3PT%': number | null | undefined;
    FT: string | number;
    'FT%': number | null | undefined;
    TO: number;
    REB: number;
    AST: number;
    STL: number;
    BLK: number;
    '+/-': number | undefined;
    'AST/TO': number | undefined;
    'eFG%': number | undefined;
    'TS%': number | undefined;
};

export type PlayerStats =
    | TeamBattingStats
    | TeamPitchingStats
    | TeamFieldingStats
    | TeamInningsPlayedStats
    | BasketBallAverages
    | BasketBallTotals;

export type TeamStats = BaseballStats | SoftballStats | BasketBallStats;
export type TeamEventStats = BaseballEventStats | BasketBallEventStats;
export type BaseballStats = {
    batting: {
        playerStats: TeamBattingStats[];
        teamStats: TeamBattingStats[];
    };
    pitching: {
        playerStats: TeamPitchingStats[];
        teamStats: TeamPitchingStats[];
    };
    fielding: {
        playerCatchingStats: TeamFieldingStats[];
        playerStats: TeamFieldingStats[];
        teamStats: TeamFieldingStats[];
        inningsPlayed: TeamInningsPlayedStats[];
    };
};
export type SoftballStats = BaseballStats;
export type BaseballEventStats = {
    batting: {
        playerStats: EventBattingStats[];
        cumulativeStats: EventBattingStats[];
    };
    pitching: {
        playerStats: EventPitchingStats[];
        cumulativeStats: EventPitchingStats[];
    };
    fielding: {
        playerCatchingStats: EventFieldingStats[];
        cumulativeCatchingStats: EventFieldingStats[];
        playerStats: EventFieldingStats[];
        cumulativeStats: EventFieldingStats[];
        inningsPlayed: EventInningsPlayedStats[];
        cumulativeInningsPlayedStats: EventInningsPlayedStats[];
        statsByPosition: FieldingByPositionStats[];
    };
    offensiveSprayCharts: SprayChartData[];
    defensiveSprayCharts: SprayChartData[];
};
